import * as THREE from 'three';
import type { SplatMesh } from '@sparkjsdev/spark';
import type { PreviewModel } from '../appearance/PreviewModel';

interface SceneBboxDebugOptions {
  scene: THREE.Scene;
  /** Optional axes helper at world origin (length in world units). Defaults to 2.0. */
  axesSize?: number;
}

interface BboxSnapshot {
  min: [number, number, number];
  max: [number, number, number];
  size: [number, number, number];
  center: [number, number, number];
}

const COLOR_PREVIEW_GLB = 0x00ffaa; // teal
const COLOR_SPLAT_CENTERS = 0xff00ff; // magenta — splat means (gaussian centers)
const COLOR_SPLAT_FULL = 0xff8800; // orange — splat full extent (centers + scale)

/**
 * Visualizes preview-GLB and splat bboxes in world-space, plus axes at origin.
 * Activated via `currentDevConfig.debugBboxes`. Logs sizes / centers to console
 * each time a refresh is requested (e.g. after splat transform UI tweaks).
 */
export class SceneBboxDebug {
  private scene: THREE.Scene;
  private previewModel: PreviewModel | null = null;
  private splatMesh: SplatMesh | null = null;
  private previewHelper: THREE.Box3Helper | null = null;
  private splatCentersHelper: THREE.Box3Helper | null = null;
  private splatFullHelper: THREE.Box3Helper | null = null;
  private axesHelper: THREE.AxesHelper | null = null;
  private disposed = false;

  constructor({ scene, axesSize = 2 }: SceneBboxDebugOptions) {
    this.scene = scene;
    if (axesSize > 0) {
      this.axesHelper = new THREE.AxesHelper(axesSize);
      this.scene.add(this.axesHelper);
    }
  }

  attachPreviewModel(previewModel: PreviewModel): void {
    if (this.disposed) return;
    this.previewModel = previewModel;
    void previewModel.loadPromise.then(() => {
      this.refreshPreviewBbox();
    }).catch(() => {
      /* preview load failed — nothing to visualize */
    });
  }

  attachSplatMesh(splatMesh: SplatMesh): void {
    if (this.disposed) return;
    this.splatMesh = splatMesh;
    this.refreshSplatBbox();
  }

  /** Re-read splat bbox & re-draw helper. Call this after splat transform changes. */
  refreshSplatBbox(): void {
    if (this.disposed || !this.splatMesh) return;
    const mesh = this.splatMesh;
    mesh.updateMatrixWorld(true);

    let centersLocal: THREE.Box3 | null = null;
    let fullLocal: THREE.Box3 | null = null;
    try {
      centersLocal = mesh.getBoundingBox(true);
      fullLocal = mesh.getBoundingBox(false);
    } catch (error) {
      console.warn('[bbox-debug] splatMesh.getBoundingBox threw', error);
      return;
    }

    if (!centersLocal || !fullLocal || centersLocal.isEmpty() || fullLocal.isEmpty()) {
      return;
    }

    const centersWorld = centersLocal.clone().applyMatrix4(mesh.matrixWorld);
    const fullWorld = fullLocal.clone().applyMatrix4(mesh.matrixWorld);

    this.removeHelper('splatCentersHelper');
    this.removeHelper('splatFullHelper');
    this.splatCentersHelper = new THREE.Box3Helper(centersWorld, COLOR_SPLAT_CENTERS);
    this.splatFullHelper = new THREE.Box3Helper(fullWorld, COLOR_SPLAT_FULL);
    this.scene.add(this.splatCentersHelper);
    this.scene.add(this.splatFullHelper);

    // numSplats lets us verify which SOG variant loaded:
    //   overall_600k: ~617480, overall_1m: ~999882, overall_2m: ~1999784
    const meshAny = mesh as unknown as {
      numSplats?: number;
      packedSplats?: { numSplats?: number };
    };
    const numSplats = meshAny.numSplats ?? meshAny.packedSplats?.numSplats ?? null;

    console.log('[bbox-debug] splat (world)', {
      numSplats,
      centers: snapshotBox(centersWorld),
      full: snapshotBox(fullWorld),
      localCenters: snapshotBox(centersLocal),
      localFull: snapshotBox(fullLocal),
      transform: {
        position: mesh.position.toArray(),
        rotation: [mesh.rotation.x, mesh.rotation.y, mesh.rotation.z] as [number, number, number],
        scale: mesh.scale.toArray(),
      },
      matrix: mesh.matrix.elements,
      matrixWorld: mesh.matrixWorld.elements,
    });
  }

  /** Re-read preview-GLB bbox & re-draw helper. */
  refreshPreviewBbox(): void {
    if (this.disposed || !this.previewModel) return;
    const box = this.previewModel.getWorldBoundingBox();
    if (!box || box.isEmpty()) return;

    this.removeHelper('previewHelper');
    this.previewHelper = new THREE.Box3Helper(box, COLOR_PREVIEW_GLB);
    this.scene.add(this.previewHelper);

    console.log('[bbox-debug] preview GLB (world)', {
      bbox: snapshotBox(box),
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeHelper('previewHelper');
    this.removeHelper('splatCentersHelper');
    this.removeHelper('splatFullHelper');
    this.removeHelper('axesHelper');
    this.previewModel = null;
    this.splatMesh = null;
    this.scene = null as unknown as THREE.Scene;
  }

  private removeHelper(
    field: 'previewHelper' | 'splatCentersHelper' | 'splatFullHelper' | 'axesHelper',
  ): void {
    const helper = this[field];
    if (!helper) return;
    if (this.scene) {
      this.scene.remove(helper);
    }
    const asLine = helper as unknown as {
      geometry?: { dispose?: () => void };
      material?: { dispose?: () => void } | { dispose?: () => void }[];
    };
    asLine.geometry?.dispose?.();
    if (Array.isArray(asLine.material)) {
      for (const m of asLine.material) m?.dispose?.();
    } else {
      asLine.material?.dispose?.();
    }
    this[field] = null;
  }
}

function snapshotBox(box: THREE.Box3): BboxSnapshot {
  const size = box.getSize(new THREE.Vector3());
  const center = box.getCenter(new THREE.Vector3());
  return {
    min: box.min.toArray() as [number, number, number],
    max: box.max.toArray() as [number, number, number],
    size: size.toArray() as [number, number, number],
    center: center.toArray() as [number, number, number],
  };
}
